/**
 * "Marcar para conferir" do Cenário do PCP: o que o PCP prometeu digitar na
 * planilha fica guardado NESTE aparelho (`tenantStorage`, por unidade) e é
 * conferido a cada leitura da planilha: aplicado, divergiu ou pendente.
 * Marcas aplicadas saem no dia seguinte; marcas com mais de 30 dias saem
 * sozinhas (regras em `utils/capacidade/reprogramacao.js`).
 *
 * A conferência acontece "na leitura seguinte da planilha" (F2.4), que pode
 * ser depois de sair da conta: a chave é DURÁVEL (`CHAVES_DURAVEIS`, não é
 * varrida no logout) e as marcas ficam separadas por usuário DENTRO do valor
 * (mesmo desenho das visões salvas dos Relatórios), então num terminal
 * compartilhado uma pessoa não vê as marcas da outra.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "../../context/AuthContext";
import { conferirMarcas, juntarMarcas } from "../../utils/capacidade";
import { getItemTenant, setItemTenant } from "../../utils/tenantStorage";

export const CHAVE_REPROGRAMACOES = "pharuerp:capacidade-fabrica:reprogramacoes:v1";

/** `{[usuarioId]: [marca]}`; valor corrompido ou storage bloqueado = sem marcas. */
function lerTodas() {
  try {
    const bruto = JSON.parse(getItemTenant(CHAVE_REPROGRAMACOES) || "{}");
    return bruto && typeof bruto === "object" && !Array.isArray(bruto) ? bruto : {};
  } catch {
    return {};
  }
}

function ler(usuarioId) {
  const lista = lerTodas()[usuarioId];
  return Array.isArray(lista) ? lista : [];
}

function gravar(usuarioId, marcas) {
  try {
    setItemTenant(CHAVE_REPROGRAMACOES, JSON.stringify({ ...lerTodas(), [usuarioId]: marcas }));
  } catch {
    // Storage cheio ou bloqueado (aba anônima): a marca vale só nesta sessão.
  }
}

const doUsuario = (usuarioId) => ({ dono: usuarioId, marcas: ler(usuarioId) });

/**
 * `{itens, marcar(novas), esquecer(marca)}`. `linhas` = linhas do extrato
 * atual (a última por chave vale); `feriadosPlanilha` = aba TABELAS, para
 * medir a divergência em dias úteis do Excel.
 */
export default function useMarcasReprogramacao({ linhas, hoje, feriadosPlanilha }) {
  const { usuario } = useAuth() || {};
  const usuarioId = String(usuario?.id ?? "anonimo");
  const [estado, setEstado] = useState(() => doUsuario(usuarioId));
  // Trocou a pessoa sem desmontar a página: as marcas passam a ser as dela
  // antes de qualquer gravação (senão as da anterior iriam para a nova).
  const atual = estado.dono === usuarioId ? estado : doUsuario(usuarioId);
  if (atual !== estado) setEstado(atual);
  const { dono, marcas } = atual;

  const conferencia = useMemo(
    () => conferirMarcas({ marcas, linhas, hoje, feriadosPlanilha }),
    [marcas, linhas, hoje, feriadosPlanilha],
  );

  // Persiste a limpeza automática (expiradas e aplicadas de ontem) e a
  // data em que cada marca foi vista aplicada.
  const { guardar } = conferencia;
  useEffect(() => {
    if (!linhas) return;
    if (JSON.stringify(guardar) !== JSON.stringify(marcas)) {
      gravar(dono, guardar);
      setEstado({ dono, marcas: guardar });
    }
  }, [guardar, marcas, linhas, dono]);

  const alterar = useCallback((transformar) => {
    setEstado((anterior) => {
      const proximas = transformar(anterior.marcas);
      gravar(anterior.dono, proximas);
      return { dono: anterior.dono, marcas: proximas };
    });
  }, []);

  const marcar = useCallback((novas) => alterar((atuais) => juntarMarcas(atuais, novas)), [alterar]);
  const esquecer = useCallback(
    (marca) => alterar((atuais) => atuais.filter((m) => !(m.chave === marca.chave && m.campo === marca.campo))),
    [alterar],
  );

  return { itens: conferencia.itens, marcar, esquecer };
}
